import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, limit, doc, deleteDoc, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { isAdmin, isPartner } from '../config/accessControl';

const PAGE_SIZE = 50;

// Helper function for compact date string in the list view (used by Kaiser/follow-up)
const formatCompactDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'No Date/Time';
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString; // Original if invalid
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  } catch (e) {
    return dateTimeString; // Fallback
  }
};

// Helper function for formatting date/time for appointment details (used by classic appointments)
const formatAppointmentDateTime = (timestampInput) => {
  if (!timestampInput) return 'N/A';
  try {
    let date;
    if (typeof timestampInput === 'string') {
      date = new Date(timestampInput);
      if (isNaN(date.getTime())) return timestampInput;
    } else if (timestampInput && typeof timestampInput.toDate === 'function') { // Firestore Timestamp
      date = timestampInput.toDate();
    } else if (timestampInput instanceof Date) {
      date = timestampInput;
    } else {
      return String(timestampInput);
    }
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    }).format(date);
  } catch (e) {
    return String(timestampInput);
  }
};

function Appointments({ selectedCompany, apiBaseUrl, apiKey, userEmail }) {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dataType, setDataType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const checkUserRoles = async () => {
      if (userEmail) {
        setIsUserAdmin(await isAdmin(userEmail));
        setIsUserPartner(await isPartner(userEmail));
      } else {
        setIsUserAdmin(false);
        setIsUserPartner(false);
      }
    };
    checkUserRoles();
  }, [userEmail]);

  useEffect(() => {
    if (selectedCompany) {
      fetchItems(true);
    } else {
      setItems([]);
      setSelectedItem(null);
      setDataType('');
      setTotalCount(0);
      setHasMore(true);
      setLastVisible(null);
    }
    setSelectedItem(null);
  }, [selectedCompany, apiBaseUrl, apiKey]);

  const fetchItems = async (isInitial = false) => {
    const companyLower = selectedCompany.toLowerCase();

    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      if (companyLower === 'smartmed_2' || companyLower === 'uki_clinic_almaty') {
        const appointmentsRef = collection(db, 'production', selectedCompany, 'appointments');
        let q;

        if (isInitial) {
          q = query(appointmentsRef, orderBy('created_at', 'desc'), limit(PAGE_SIZE));
          const countSnapshot = await getCountFromServer(appointmentsRef);
          setTotalCount(countSnapshot.data().count);
        } else {
          q = query(appointmentsRef, orderBy('created_at', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
        }

        const querySnapshot = await getDocs(q);
        const appointmentsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter smartmed_2 by doc_id prefix, uki_clinic_almaty shows all
        const filteredAppointments = companyLower === 'smartmed_2'
          ? appointmentsList.filter(appt => appt.doc_id && appt.doc_id.startsWith('smartmed_2_'))
          : appointmentsList;

        if (isInitial) {
          setItems(filteredAppointments);
          setDataType('appointment');
          if (filteredAppointments.length > 0) setSelectedItem(filteredAppointments[0]);
          else setSelectedItem(null);
        } else {
          setItems(prev => [...prev, ...filteredAppointments]);
        }

        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === PAGE_SIZE);

      } else if (companyLower === 'summit care clinic') {
        const appointmentsRef = collection(db, 'production', selectedCompany, 'appointments');
        let q;

        if (isInitial) {
          q = query(appointmentsRef, orderBy('created_at', 'desc'), limit(PAGE_SIZE));
          const countSnapshot = await getCountFromServer(appointmentsRef);
          setTotalCount(countSnapshot.data().count);
        } else {
          q = query(appointmentsRef, orderBy('created_at', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
        }

        const querySnapshot = await getDocs(q);
        const appointmentsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (isInitial) {
          setItems(appointmentsList);
          setDataType('appointment');
          if (appointmentsList.length > 0) setSelectedItem(appointmentsList[0]);
          else setSelectedItem(null);
        } else {
          setItems(prev => [...prev, ...appointmentsList]);
        }

        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === PAGE_SIZE);

      } else {
        const followUpsRef = collection(db, 'production', selectedCompany, 'follow_up_results');
        let q;

        if (isInitial) {
          q = query(followUpsRef, orderBy('start_time', 'desc'), limit(PAGE_SIZE));
          const countSnapshot = await getCountFromServer(followUpsRef);
          setTotalCount(countSnapshot.data().count);
        } else {
          q = query(followUpsRef, orderBy('start_time', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
        }

        const querySnapshot = await getDocs(q);
        const followUpsList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          const extracted = data.extracted_data || {};
          return {
            id: doc.id,
            name: data.target_name || 'N/A',
            phone: data.target_phone || 'N/A',
            created_at: data.start_time,
            call_sid: data.call_sid || 'N/A',
            follow_up_type: data.follow_up_type || extracted.follow_up_type || 'N/A',
            additional_treatment_in_hospital: extracted.additional_treatment_in_hospital,
            call_outcome_summary: extracted.call_outcome_summary || 'No summary available',
            condition_details: extracted.condition_details,
            confirmed_condition: extracted.confirmed_condition,
            had_reoperation: extracted.had_reoperation,
            readmitted_to_hospital: extracted.readmitted_to_hospital,
            reason_for_visit: extracted.reason_for_visit,
            returned_to_normal_activities: extracted.returned_to_normal_activities,
            saw_doctor_post_surgery: extracted.saw_doctor_post_surgery,
            doc_id: doc.id,
          };
        });

        if (isInitial) {
          setItems(followUpsList);
          setDataType('followUp');
          if (followUpsList.length > 0) setSelectedItem(followUpsList[0]);
          else setSelectedItem(null);
        } else {
          setItems(prev => [...prev, ...followUpsList]);
        }

        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === PAGE_SIZE);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
  };

  const handleDeleteItem = async (itemId, itemCompany, itemDataType) => {
    const itemTypeName = itemDataType === 'appointment' ? 'appointment' : 'follow-up';
    if (!window.confirm(`Are you sure you want to delete this ${itemTypeName}? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(itemId);
    setError(null);

    try {
      const companyLower = itemCompany.toLowerCase();

      if (itemDataType === 'appointment') {
        // For smartmed_2, appointments are in Firebase, not PostgreSQL
        await deleteDoc(doc(db, 'production', itemCompany, 'appointments', itemId));
      } else if (itemDataType === 'followUp') {
        await deleteDoc(doc(db, 'production', itemCompany, 'follow_up_results', itemId));
      }

      setItems(prevItems => prevItems.filter(item => item.id !== itemId));
      if (selectedItem?.id === itemId) {
        const newItems = items.filter(item => item.id !== itemId);
        setSelectedItem(newItems.length > 0 ? newItems[0] : null);
      }

    } catch (err) {
      console.error(`Error deleting ${itemTypeName}:`, err);
      setError(`Failed to delete ${itemTypeName}: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };
  
  const getTitle = () => {
    if (!selectedCompany) return 'Items';
    const name = selectedCompany.charAt(0).toUpperCase() + selectedCompany.slice(1);
    return dataType === 'appointment' ? `${name} Appointments` : `${name} Follow-ups`;
  };

  if (!selectedCompany) {
    return <div className="p-4 text-gray-500">Please select a company to view items.</div>;
  }

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Left panel - List of items */}
      <div className="w-full md:w-1/3 bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">{getTitle()}</h3>
          <p className="text-sm text-gray-500">
            {totalCount > 0 ? `${totalCount} total` : `${items.length} results`}
            {items.length > 0 && items.length < totalCount && ` (${items.length} loaded)`}
          </p>
        </div>

        {loading && <div className="p-4 text-center text-gray-500">Loading...</div>}
        {error && (
          <div className="p-4 m-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
            <button onClick={() => fetchItems(true)} className="ml-4 px-2 py-1 bg-red-200 rounded hover:bg-red-300 text-sm">
              Retry
            </button>
          </div>
        )}

        <div className="overflow-y-auto max-h-[60vh]">
          {items.map((item) => (
            <div
              key={item.id}
              className={`block p-4 border-b cursor-pointer hover:bg-gray-100 transition duration-150 ease-in-out ${selectedItem?.id === item.id ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
              onClick={() => handleSelectItem(item)}
            >
              {dataType === 'appointment' ? (
                <>
              <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-gray-800 truncate" title={item.name}>{item.name || 'Unnamed Appointment'}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                      {item.day || 'No Date'}, {item.time || 'No Time'}
                </span>
              </div>
                  <div className="text-sm text-gray-600 truncate" title={item.complaints}>{item.complaints || 'No complaints'}</div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-gray-800 truncate" title={item.name}>{item.name}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{formatCompactDateTime(item.created_at)}</span>
              </div>
                  <div className="text-sm text-gray-600 truncate" title={item.call_outcome_summary}>{item.call_outcome_summary}</div>
                </>
              )}
            </div>
          ))}
          {items.length === 0 && !loading && !error && (
            <div className="p-4 text-center text-gray-500">
              No data found for {selectedCompany}.
            </div>
          )}

          {hasMore && items.length > 0 && (
            <div className="p-4 text-center border-t">
              <button
                onClick={() => fetchItems(false)}
                disabled={loadingMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : 'Show more'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel - Item details */}
      <div className="w-full md:w-2/3 bg-white rounded-lg shadow">
        {selectedItem ? (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <div>
              <h3 className="text-lg font-medium text-gray-900">
                    {dataType === 'appointment' ? 'Appointment Details' : 'Follow-up Details'}
              </h3>
                  <p className="text-xl font-semibold text-gray-800 mt-1">{selectedItem.name || 'N/A'}</p>
                <p className="text-sm text-gray-500">
                    {dataType === 'appointment' 
                      ? `${selectedItem.day || 'N/A'}, ${selectedItem.time || 'N/A'}`
                      : formatAppointmentDateTime(selectedItem.created_at)}
                  </p>
                </div>
                {(isUserAdmin || isUserPartner) && (
                  <button
                    onClick={() => handleDeleteItem(selectedItem.id, selectedCompany, dataType)}
                    disabled={deletingId === selectedItem.id}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed self-start"
                  >
                    {deletingId === selectedItem.id ? 'Deleting...' : `Delete ${dataType === 'appointment' ? 'Appointment' : 'Follow-up'}`}
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-grow">
              {dataType === 'appointment' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InfoItem label="Phone" value={selectedItem.phone} />
                    <InfoItem label="Likely appropriate tests / exams" value={selectedItem.service} />
                    <InfoItem label="Doctor" value={selectedItem.doctor} />
                    <InfoItem label="Price" value={selectedItem.price} />
                    <InfoItem label="Company" value={selectedItem.company} />
                    <InfoItem label="Created At" value={formatAppointmentDateTime(selectedItem.created_at)} />
                  </div>
                  <InfoSection label="Complaints" value={selectedItem.complaints} />
                  <InfoSection label="Most Likely Causes" value={selectedItem.most_likely_causes} />
                  {selectedItem.address && <InfoItem label="Address" value={selectedItem.address} />}
                  <InfoItem label="Conversation ID" value={selectedItem.conversation_id} breakWord={true} />
                  <InfoItem label="Document ID" value={selectedItem.doc_id} breakWord={true} />
                </>
              ) : (
                <>
                  <InfoItem label="Phone" value={selectedItem.phone} />
                  <InfoItem label="Follow-up Type" value={selectedItem.follow_up_type} />
                  <InfoSection label="Call Outcome Summary" value={selectedItem.call_outcome_summary} />
                  <InfoItem label="Returned to Normal Activities" value={selectedItem.returned_to_normal_activities} />
                  <InfoItem label="Saw Doctor Post Surgery" value={selectedItem.saw_doctor_post_surgery} />
                  <InfoItem label="Reason for Visit" value={selectedItem.reason_for_visit} />
                  <InfoItem label="Condition Details" value={selectedItem.condition_details} />
                  <InfoItem label="Confirmed Condition" value={selectedItem.confirmed_condition} />
                  <InfoItem label="Had Reoperation" value={selectedItem.had_reoperation} />
                  <InfoItem label="Readmitted to Hospital" value={selectedItem.readmitted_to_hospital} />
                  <InfoItem label="Additional Treatment in Hospital" value={selectedItem.additional_treatment_in_hospital} />
                  <InfoItem label="Call SID" value={selectedItem.call_sid} breakWord={true} />
                  <InfoItem label="Document ID" value={selectedItem.doc_id} breakWord={true} />
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 flex items-center justify-center h-full">
            {loading ? 'Loading...' : 'Select an item to view details.'}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component for consistent styling of info items
const InfoItem = ({ label, value, breakWord = false }) => (
  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</h4>
    <p className={`text-sm font-medium text-gray-800 ${breakWord ? 'break-all' : ''}`}>{value !== null && value !== undefined ? String(value) : 'N/A'}</p>
  </div>
);

// Helper component for longer text sections
const InfoSection = ({ label, value }) => (
  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
     <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</h4>
     <p className="text-sm font-medium text-gray-800 whitespace-pre-wrap">{value !== null && value !== undefined ? String(value) : 'N/A'}</p>
  </div>
)

export default Appointments;